import { getCarById, getTrackById } from '../content';
import { AudioManager } from '../audio/AudioManager';
import { InputManager } from '../input/InputManager';
import { RaceManager, VehicleController, type VehicleUpdateResult } from '../racing';
import type { GameRenderBridge } from '../render/contracts';
import { SaveSystem } from '../storage/SaveSystem';
import { TrackRuntime } from '../tracks';
import { DEFAULT_BINDINGS } from './constants';
import type {
  GameSettings,
  GameSnapshot,
  InputAction,
  InputState,
  RaceResult,
  ScreenId,
  VehicleState,
} from './types';

type Listener = () => void;

const EMPTY_INPUT: InputState = {
  throttle: 0,
  brake: 0,
  steer: 0,
  handbrake: false,
  restartPressed: false,
  resetPressed: false,
  pausePressed: false,
};

function distance(a: { x: number; y: number; z: number }, b: { x: number; y: number; z: number }): number {
  return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
}

function copyInput(input: InputState): InputState {
  return { ...input };
}

export class GameController implements GameRenderBridge {
  private readonly saveSystem = new SaveSystem();
  private readonly inputManager: InputManager;
  private readonly audioManager = new AudioManager();
  private readonly listeners = new Set<Listener>();
  private snapshot: GameSnapshot;
  private trackRuntime: TrackRuntime | null = null;
  private vehicle: VehicleController | null = null;
  private raceManager: RaceManager | null = null;
  private lastInput: InputState = { ...EMPTY_INPUT };
  private lastCountdownText: string | null = null;
  private lastCheckpointCount = 0;
  private finishContact = false;
  private uiEmitAccumulator = 0;
  private noticeRemaining = 0;
  private toastRemaining = 0;
  private started = false;
  private menuClickHandler: ((event: Event) => void) | null = null;
  private splashKeyHandler: ((event: KeyboardEvent) => void) | null = null;

  constructor() {
    const save = this.saveSystem.load();
    const selectedCarId = getCarById(save.selectedCarId).id;
    const selectedTrackId = getTrackById(save.selectedTrackId).id;
    this.inputManager = new InputManager(save.settings.bindings);
    const bindings = this.inputManager.getBindings();
    this.snapshot = {
      screen: 'splash',
      previousScreen: null,
      selectedCarId,
      selectedTrackId,
      settings: { ...save.settings, bindings },
      bestTimes: { ...save.bestTimes },
      race: null,
      result: null,
      paused: false,
      settingsFromPause: false,
      transitionKey: 0,
      toast: null,
    };
  }

  readonly subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getSnapshot = (): GameSnapshot => this.snapshot;

  start(): void {
    if (this.started) return;
    this.started = true;
    this.inputManager.attach(window);
    this.audioManager.applySettings(this.snapshot.settings);
    this.audioManager.installGestureUnlock(window);
    this.applyDisplaySettings(this.snapshot.settings, true);

    this.menuClickHandler = (event) => {
      const target = event.target;
      if (target instanceof Element && target.closest('button')) this.audioManager.playMenuSelect();
    };
    document.addEventListener('click', this.menuClickHandler);
    this.splashKeyHandler = (event) => {
      if (this.snapshot.screen === 'splash') {
        // Prevent the same Enter/Space keystroke from activating the newly
        // focused Play button after the splash unmounts.
        event.preventDefault();
        event.stopImmediatePropagation();
        this.dismissSplash();
      }
    };
    window.addEventListener('keydown', this.splashKeyHandler);

    if (import.meta.env.DEV) {
      window.__SLIPSTREAM_DEBUG__ = {
        getSnapshot: this.getSnapshot,
        getVehicleState: () => this.getVehicleState(),
        selectTrack: (id: string) => this.selectTrack(id),
        selectCar: (id: string) => this.selectCar(id),
        startRace: () => this.startRace(),
        completeRace: () => {
          if (!this.raceManager || !this.vehicle || !this.trackRuntime) return;
          if (this.raceManager.getSnapshot().phase === 'countdown') this.raceManager.update(3.1);
          while (this.raceManager.getSnapshot().completedCheckpoints < this.trackRuntime.checkpoints.length) {
            this.raceManager.passCheckpoint(this.raceManager.getSnapshot().completedCheckpoints);
          }
          const attempt = this.raceManager.tryFinish();
          if (attempt.accepted && attempt.result) this.completeRace(attempt.result);
        },
        advanceToProgress: (progress: number) => {
          if (!this.vehicle || !this.trackRuntime) return;
          const sample = this.trackRuntime.sampleAt(Math.min(1, Math.max(0, progress)));
          const yaw = Math.atan2(sample.forward.x, sample.forward.z);
          this.vehicle.teleport({ position: sample.position, yaw, pitch: 0, roll: sample.bank, progress: sample.progress });
        },
      };
    }
    this.emit();
  }

  dispose(): void {
    this.inputManager.dispose();
    void this.audioManager.dispose();
    if (this.menuClickHandler) document.removeEventListener('click', this.menuClickHandler);
    if (this.splashKeyHandler) window.removeEventListener('keydown', this.splashKeyHandler);
    this.menuClickHandler = null;
    this.splashKeyHandler = null;
    this.listeners.clear();
    this.started = false;
    if (import.meta.env.DEV) delete window.__SLIPSTREAM_DEBUG__;
  }

  dismissSplash(): void {
    if (this.snapshot.screen !== 'splash') return;
    this.navigate('menu', null);
  }

  startPlayFlow(): void {
    this.navigate('track-select');
  }

  showMainMenu(): void {
    this.stopRacePresentation();
    this.navigate('menu', this.snapshot.screen);
  }

  openTrackSelect(): void {
    this.stopRacePresentation();
    this.navigate('track-select');
  }

  openCarSelect(): void {
    this.stopRacePresentation();
    this.navigate('car-select');
  }

  openSettings(): void {
    this.snapshot = {
      ...this.snapshot,
      previousScreen: this.snapshot.screen,
      screen: 'settings',
      settingsFromPause: false,
      transitionKey: this.snapshot.transitionKey + 1,
    };
    this.emit();
  }

  openPauseSettings(): void {
    if (!this.snapshot.paused || !this.raceManager) return;
    this.snapshot = {
      ...this.snapshot,
      previousScreen: 'race',
      screen: 'settings',
      settingsFromPause: true,
      transitionKey: this.snapshot.transitionKey + 1,
    };
    this.emit();
  }

  closeSettings(): void {
    if (this.snapshot.settingsFromPause) {
      this.snapshot = {
        ...this.snapshot,
        screen: 'race',
        previousScreen: null,
        settingsFromPause: false,
        paused: true,
        transitionKey: this.snapshot.transitionKey + 1,
      };
      this.emit();
      return;
    }
    this.back();
  }

  back(): void {
    const current = this.snapshot.screen;
    const fallback: ScreenId = current === 'car-select' ? 'track-select' : 'menu';
    const target = this.snapshot.previousScreen && this.snapshot.previousScreen !== current
      ? this.snapshot.previousScreen
      : fallback;
    this.snapshot = {
      ...this.snapshot,
      screen: target,
      previousScreen: target === 'menu' ? null : 'menu',
      settingsFromPause: false,
      transitionKey: this.snapshot.transitionKey + 1,
    };
    this.emit();
  }

  selectTrack(trackId: string): void {
    const selectedTrackId = getTrackById(trackId).id;
    if (selectedTrackId === this.snapshot.selectedTrackId) return;
    const save = this.saveSystem.setSelectedTrack(selectedTrackId);
    this.snapshot = {
      ...this.snapshot,
      selectedTrackId,
      bestTimes: { ...save.bestTimes },
      transitionKey: this.snapshot.transitionKey + 1,
    };
    this.emit();
  }

  selectCar(carId: string): void {
    const selectedCarId = getCarById(carId).id;
    if (selectedCarId === this.snapshot.selectedCarId) return;
    this.saveSystem.setSelectedCar(selectedCarId);
    this.snapshot = {
      ...this.snapshot,
      selectedCarId,
      transitionKey: this.snapshot.transitionKey + 1,
    };
    this.emit();
  }

  confirmTrack(): void {
    this.navigate('car-select');
  }

  startRace(): void {
    const track = getTrackById(this.snapshot.selectedTrackId);
    const car = getCarById(this.snapshot.selectedCarId);
    this.trackRuntime = new TrackRuntime(track);
    this.raceManager = new RaceManager({
      trackId: track.id,
      carId: car.id,
      startTransform: this.trackRuntime.getStartTransform(),
      checkpoints: this.trackRuntime.checkpoints,
      bestTimeMs: this.snapshot.bestTimes[track.id] ?? null,
    });
    this.vehicle = new VehicleController({
      physics: car.physics,
      track: this.trackRuntime,
      initialTransform: this.trackRuntime.getStartTransform(),
      rideHeight: 0,
    });
    this.lastCountdownText = null;
    this.lastCheckpointCount = 0;
    this.finishContact = false;
    this.noticeRemaining = 0;
    this.lastInput = { ...EMPTY_INPUT };
    this.snapshot = {
      ...this.snapshot,
      screen: 'race',
      previousScreen: 'car-select',
      race: this.raceManager.getHudState(this.vehicle.getTelemetry()),
      result: null,
      paused: false,
      settingsFromPause: false,
      transitionKey: this.snapshot.transitionKey + 1,
    };
    this.emit();
  }

  restartRace(): void {
    if (!this.raceManager || !this.vehicle) {
      this.startRace();
      return;
    }
    const transform = this.raceManager.restart(this.snapshot.bestTimes[this.snapshot.selectedTrackId] ?? null);
    this.vehicle.restartAt(transform);
    this.raceManager.setPaused(false);
    this.lastCountdownText = null;
    this.lastCheckpointCount = 0;
    this.finishContact = false;
    this.noticeRemaining = 0;
    this.snapshot = {
      ...this.snapshot,
      screen: 'race',
      race: this.raceManager.getHudState(this.vehicle.getTelemetry()),
      result: null,
      paused: false,
      settingsFromPause: false,
      transitionKey: this.snapshot.transitionKey + 1,
    };
    this.emit();
  }

  retryRace(): void {
    this.restartRace();
  }

  resetVehicle(): void {
    if (!this.raceManager || !this.vehicle) return;
    this.vehicle.resetTo(this.raceManager.resetToCheckpoint());
    this.noticeRemaining = 1.6;
    this.audioManager.playLanding(0.35);
    this.refreshRaceSnapshot(true);
  }

  pauseRace(): void {
    if (!this.raceManager || this.snapshot.screen !== 'race') return;
    this.raceManager.setPaused(true);
    this.audioManager.stopEngine();
    this.snapshot = {
      ...this.snapshot,
      paused: true,
      race: this.raceManager.getHudState(this.vehicle!.getTelemetry()),
      transitionKey: this.snapshot.transitionKey + 1,
    };
    this.emit();
  }

  resumeRace(): void {
    if (!this.raceManager) return;
    this.raceManager.setPaused(false);
    this.snapshot = {
      ...this.snapshot,
      screen: 'race',
      paused: false,
      settingsFromPause: false,
      race: this.raceManager.getHudState(this.vehicle!.getTelemetry()),
      transitionKey: this.snapshot.transitionKey + 1,
    };
    this.emit();
  }

  returnToTrackSelect(): void {
    this.stopRacePresentation();
    this.navigate('track-select', 'menu');
  }

  updateSettings(update: Partial<GameSettings>): void {
    const next: GameSettings = {
      ...this.snapshot.settings,
      ...update,
      bindings: update.bindings ?? this.snapshot.settings.bindings,
    };
    const bindings = this.inputManager.setBindings(next.bindings);
    const save = this.saveSystem.updateSettings({ ...next, bindings });
    this.audioManager.applySettings(save.settings);
    this.applyDisplaySettings(save.settings, true);
    this.snapshot = {
      ...this.snapshot,
      settings: save.settings,
      transitionKey: this.snapshot.transitionKey + (update.sceneryDensity ? 1 : 0),
    };
    this.emit();
  }

  rebindAction(action: InputAction, code: string): void {
    const bindings = this.inputManager.rebind(action, code);
    this.updateSettings({ bindings });
    this.setToast(`${action.replace(/([A-Z])/g, ' $1')} bound to ${code}`);
  }

  resetBindings(): void {
    const bindings = this.inputManager.setBindings(DEFAULT_BINDINGS);
    this.updateSettings({ bindings });
    this.setToast('Default controls restored');
  }

  previewAudio(): void {
    void this.audioManager.unlock().then((ready) => {
      if (ready) {
        this.audioManager.playRaceStart();
        window.setTimeout(() => this.audioManager.playBooster(0.7), 220);
      }
    });
  }

  quit(): void {
    if (window.desktop) {
      window.desktop.quit();
      return;
    }
    window.close();
    this.setToast('Use your browser controls to close this tab');
  }

  tick(deltaSeconds: number): void {
    const delta = Math.min(0.05, Math.max(0, Number.isFinite(deltaSeconds) ? deltaSeconds : 0));
    const input = this.inputManager.consumeState();
    this.lastInput = copyInput(input);
    this.updateToast(delta);

    if (this.snapshot.screen === 'settings' && this.snapshot.settingsFromPause) {
      if (input.pausePressed) this.closeSettings();
      return;
    }

    if (this.snapshot.screen !== 'race' || !this.vehicle || !this.raceManager || !this.trackRuntime) {
      this.handleNonRaceEscape(input);
      this.audioManager.stopEngine(0.2);
      return;
    }

    if (input.pausePressed) {
      if (this.snapshot.paused) this.resumeRace();
      else this.pauseRace();
      return;
    }
    if (input.restartPressed) {
      this.restartRace();
      return;
    }
    if (input.resetPressed) {
      this.resetVehicle();
      return;
    }
    if (this.snapshot.paused) return;

    this.raceManager.update(delta);
    const result = this.vehicle.update(input, delta, {
      controlsEnabled: this.raceManager.canDrive(),
      paused: false,
    });
    this.processVehicleEvents(result);
    this.processRaceTriggers();
    if (this.snapshot.screen !== 'race' || !this.vehicle || !this.raceManager) return;

    const telemetry = this.vehicle.getTelemetry();
    this.audioManager.updateEngine(telemetry.speedMps, input.throttle);
    if (telemetry.sliding > 0.42 && telemetry.grounded) this.audioManager.playTireSlide(telemetry.sliding);
    this.processCountdownAudio();
    this.updateNotice(delta);
    this.refreshRaceSnapshot(false);
    this.uiEmitAccumulator += delta;
    if (this.uiEmitAccumulator >= 1 / 30) {
      this.uiEmitAccumulator = 0;
      this.emit();
    }
  }

  getTrackRuntime(): TrackRuntime | null {
    return this.trackRuntime;
  }

  getVehicleState(): VehicleState | null {
    return this.vehicle?.getState() ?? null;
  }

  getLastInput(): InputState {
    return copyInput(this.lastInput);
  }

  private processVehicleEvents(result: VehicleUpdateResult): void {
    if (result.boosterTriggered) this.audioManager.playBooster(1);
    if (result.landingImpact > 2.2) this.audioManager.playLanding(Math.min(1, result.landingImpact / 15));
    const recovery = this.vehicle?.consumeRecoverySignal();
    if (recovery) {
      this.resetVehicle();
      this.setToast(
        recovery.reason === 'fell'
          ? 'Recovered from the fall'
          : recovery.reason === 'upside-down'
            ? 'Car safely reset'
            : 'Returned to the course',
      );
    }
  }

  private processRaceTriggers(): void {
    if (!this.vehicle || !this.raceManager || !this.trackRuntime || !this.raceManager.canDrive()) return;
    const state = this.vehicle.getState();
    const race = this.raceManager.getSnapshot();
    const expected = this.trackRuntime.checkpoints[race.completedCheckpoints];
    if (expected && distance(state.position, expected.position) <= expected.triggerRadius) {
      const attempt = this.raceManager.passCheckpoint(expected);
      if (attempt.accepted) {
        this.lastCheckpointCount = attempt.completedCheckpoints;
        this.noticeRemaining = 1.45;
        this.audioManager.playCheckpoint();
      }
    }

    const finish = this.trackRuntime.getFinishTransform();
    const finishRadius = Math.max(7, this.trackRuntime.sampleAt(1).width * 0.62);
    const atFinish = state.progress > 0.965 && distance(state.position, finish.position) <= finishRadius;
    if (atFinish && !this.finishContact) {
      const finishAttempt = this.raceManager.tryFinish();
      if (finishAttempt.accepted && finishAttempt.result) this.completeRace(finishAttempt.result);
      else this.noticeRemaining = 2.4;
    }
    this.finishContact = atFinish;
  }

  private completeRace(result: RaceResult): void {
    const update = this.saveSystem.recordBestTime(result.trackId, result.finalTimeMs);
    this.audioManager.stopEngine();
    this.audioManager.playFinish();
    this.snapshot = {
      ...this.snapshot,
      screen: 'results',
      previousScreen: 'race',
      bestTimes: { ...update.saveData.bestTimes },
      race: this.raceManager?.getHudState(this.vehicle!.getTelemetry()) ?? this.snapshot.race,
      result,
      paused: false,
      transitionKey: this.snapshot.transitionKey + 1,
    };
    this.emit();
  }

  private processCountdownAudio(): void {
    if (!this.raceManager) return;
    const text = this.raceManager.getSnapshot().countdownText;
    if (text === this.lastCountdownText) return;
    this.lastCountdownText = text;
    if (text === '3' || text === '2' || text === '1') this.audioManager.playCountdown(Number(text) as 3 | 2 | 1);
    if (text === 'GO') this.audioManager.playRaceStart();
  }

  private updateNotice(delta: number): void {
    if (!this.raceManager || this.noticeRemaining <= 0) return;
    this.noticeRemaining = Math.max(0, this.noticeRemaining - delta);
    if (this.noticeRemaining === 0) this.raceManager.clearNotice();
  }

  private refreshRaceSnapshot(emit: boolean): void {
    if (!this.raceManager || !this.vehicle) return;
    this.snapshot = {
      ...this.snapshot,
      race: this.raceManager.getHudState(this.vehicle.getTelemetry()),
      paused: this.raceManager.isPaused(),
    };
    if (emit) this.emit();
  }

  private handleNonRaceEscape(input: InputState): void {
    if (!input.pausePressed || this.snapshot.screen === 'splash' || this.snapshot.screen === 'menu') return;
    if (this.snapshot.screen === 'settings') this.closeSettings();
    else this.back();
  }

  private stopRacePresentation(): void {
    this.audioManager.stopEngine();
    if (this.raceManager) this.raceManager.setPaused(false);
    this.snapshot = { ...this.snapshot, paused: false, settingsFromPause: false };
  }

  private navigate(screen: ScreenId, previousScreen: ScreenId | null = this.snapshot.screen): void {
    this.snapshot = {
      ...this.snapshot,
      screen,
      previousScreen,
      paused: false,
      settingsFromPause: false,
      transitionKey: this.snapshot.transitionKey + 1,
    };
    this.emit();
  }

  private applyDisplaySettings(settings: GameSettings, resize: boolean): void {
    if (!window.desktop) return;
    if (resize) {
      const [width, height] = settings.resolution.split('x').map(Number);
      void window.desktop.setResolution(width, height);
    }
    void window.desktop.setFullscreen(settings.fullscreen);
  }

  private setToast(message: string): void {
    this.toastRemaining = 2.4;
    this.snapshot = { ...this.snapshot, toast: message };
    this.emit();
  }

  private updateToast(delta: number): void {
    if (this.toastRemaining <= 0) return;
    this.toastRemaining = Math.max(0, this.toastRemaining - delta);
    if (this.toastRemaining === 0 && this.snapshot.toast) {
      this.snapshot = { ...this.snapshot, toast: null };
      this.emit();
    }
  }

  private emit(): void {
    this.listeners.forEach((listener) => listener());
  }

}
